import React, { useEffect, useState } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const CATEGORIES = ['ELECTRICAL', 'PLUMBING', 'HVAC', 'MECHANICAL', 'GENERAL'];
const UOM_OPTIONS = ['NOS', 'MTR', 'KG', 'LTR', 'SET'];

const emptyForm = {
  part_code: '',
  part_name: '',
  category: 'GENERAL',
  uom: 'NOS',
  current_stock: 0,
  reorder_level: 0,
  unit_cost: 0,
  storage_location: '',
  is_active: true
};

function formatCurrency(val) {
  const num = parseFloat(val);
  if (isNaN(num)) return '0.00';
  return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function MaintSpareParts({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [search, setSearch] = useState('');

  // Create / Edit modal
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });

  // Stock adjust modal
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ adjustment: 0, reason: '' });

  const headers = () => ({
    Authorization: 'Bearer ' + token,
    'x-chain-id': selectedProperty?.chain_id,
    'x-property-id': selectedProperty?.id
  });

  const jsonHeaders = () => ({
    ...headers(),
    'Content-Type': 'application/json'
  });

  /* ---- data loading ---- */
  const loadRows = async () => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    try {
      const res = await fetch('/api/v1/maintenance/spare-parts', { headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || 'Failed to load spare parts'); setLoading(false); return; }
      setRows(Array.isArray(data) ? data : (data.rows || []));
    } catch (err) {
      setError(err?.message || 'Network error');
    }
    setLoading(false);
  };

  useEffect(() => { loadRows(); }, [selectedProperty?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (successMsg) { const t = setTimeout(() => setSuccessMsg(''), 3000); return () => clearTimeout(t); } }, [successMsg]);
  useEffect(() => { if (error) { const t = setTimeout(() => setError(''), 5000); return () => clearTimeout(t); } }, [error]);

  /* ---- create / edit ---- */
  const openCreate = () => {
    setForm({ ...emptyForm });
    setEditingId(null);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setForm({
      part_code: row.part_code || '',
      part_name: row.part_name || '',
      category: row.category || 'GENERAL',
      uom: row.uom || 'NOS',
      current_stock: row.current_stock ?? 0,
      reorder_level: row.reorder_level ?? 0,
      unit_cost: row.unit_cost ?? 0,
      storage_location: row.storage_location || '',
      is_active: row.is_active !== false
    });
    setEditingId(row.id);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.part_code.trim() || !form.part_name.trim()) {
      setError('Part Code and Part Name are required');
      return;
    }
    try {
      const url = editingId
        ? `/api/v1/maintenance/spare-parts/${editingId}`
        : '/api/v1/maintenance/spare-parts';
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: jsonHeaders(),
        body: JSON.stringify({
          ...form,
          current_stock: Number(form.current_stock) || 0,
          reorder_level: Number(form.reorder_level) || 0,
          unit_cost: Number(form.unit_cost) || 0
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || 'Save failed'); return; }
      setSuccessMsg(editingId ? 'Spare part updated' : 'Spare part created');
      setShowModal(false);
      loadRows();
    } catch (err) {
      setError(err?.message || 'Network error');
    }
  };

  /* ---- stock adjust ---- */
  const openStockAdjust = (row) => {
    setAdjustTarget(row);
    setAdjustForm({ adjustment: 0, reason: '' });
    setShowAdjustModal(true);
  };

  const handleStockAdjust = async () => {
    if (!adjustForm.adjustment || Number(adjustForm.adjustment) === 0) {
      setError('Adjustment quantity must be non-zero');
      return;
    }
    if (!adjustForm.reason.trim()) {
      setError('Reason is required for stock adjustment');
      return;
    }
    try {
      const res = await fetch(`/api/v1/maintenance/spare-parts/${adjustTarget.id}/stock-adjust`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({
          adjustment: Number(adjustForm.adjustment),
          reason: adjustForm.reason.trim()
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || 'Stock adjustment failed'); return; }
      setSuccessMsg(`Stock adjusted for ${adjustTarget.part_name}`);
      setShowAdjustModal(false);
      loadRows();
    } catch (err) {
      setError(err?.message || 'Network error');
    }
  };

  /* ---- filter ---- */
  const filtered = rows.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.part_code || '').toLowerCase().includes(s)
      || (r.part_name || '').toLowerCase().includes(s)
      || (r.category || '').toLowerCase().includes(s);
  });

  /* ---- stock level style ---- */
  const stockRowStyle = (row) => {
    const stock = Number(row.current_stock) || 0;
    const reorder = Number(row.reorder_level) || 0;
    if (reorder > 0 && stock <= reorder) {
      if (stock === 0) return { backgroundColor: '#fecaca' }; // red
      return { backgroundColor: '#fef3c7' }; // yellow
    }
    return {};
  };

  /* ---- styles ---- */
  const s = {
    page: { padding: 24, fontFamily: 'Inter, sans-serif', maxWidth: 1200, margin: '0 auto' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 },
    title: { fontSize: 22, fontWeight: 700, margin: 0 },
    btn: { padding: '8px 16px', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 },
    btnPrimary: { backgroundColor: '#2563eb', color: '#fff' },
    btnSuccess: { backgroundColor: '#16a34a', color: '#fff' },
    btnSecondary: { backgroundColor: '#e5e7eb', color: '#1f2937' },
    btnDanger: { backgroundColor: '#dc2626', color: '#fff' },
    btnSmall: { padding: '4px 10px', fontSize: 12 },
    searchBox: { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, width: isMobile ? '100%' : 260 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { textAlign: 'left', padding: '10px 8px', borderBottom: '2px solid #d1d5db', backgroundColor: '#f9fafb', fontWeight: 600, whiteSpace: 'nowrap' },
    td: { padding: '8px', borderBottom: '1px solid #e5e7eb', verticalAlign: 'middle' },
    badge: (active) => ({
      display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
      backgroundColor: active ? '#dcfce7' : '#fee2e2', color: active ? '#166534' : '#991b1b'
    }),
    overlay: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
    },
    modal: { backgroundColor: '#fff', borderRadius: 10, padding: isMobile ? 16 : 24, width: isMobile ? '100%' : 520, ...(isMobile ? { maxWidth: '95vw', boxSizing: 'border-box' } : {}), maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 4px 24px rgba(0,0,0,0.18)' },
    modalSmall: { backgroundColor: '#fff', borderRadius: 10, padding: isMobile ? 16 : 24, width: isMobile ? '100%' : 380, ...(isMobile ? { maxWidth: '95vw', boxSizing: 'border-box' } : {}), boxShadow: '0 4px 24px rgba(0,0,0,0.18)' },
    modalTitle: { fontSize: 18, fontWeight: 700, marginBottom: 16, marginTop: 0 },
    formGroup: { marginBottom: 12 },
    label: { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#374151' },
    input: { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' },
    select: { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box', backgroundColor: '#fff' },
    formRow: { display: 'flex', gap: 12 },
    formCol: { flex: 1 },
    alertError: { padding: '10px 14px', backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: 6, marginBottom: 12, fontSize: 13 },
    alertSuccess: { padding: '10px 14px', backgroundColor: '#dcfce7', color: '#166534', borderRadius: 6, marginBottom: 12, fontSize: 13 },
    lowStockNote: { display: 'flex', gap: 16, fontSize: 12, color: '#6b7280', marginBottom: 8 },
    legendDot: (color) => ({ display: 'inline-block', width: 12, height: 12, borderRadius: 3, backgroundColor: color, marginRight: 4, verticalAlign: 'middle' })
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h2 style={s.title}>Spare Parts Inventory</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input
            style={s.searchBox}
            placeholder="Search by code, name, category..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <button style={{ ...s.btn, ...s.btnPrimary }} onClick={openCreate}>+ Add Spare Part</button>
        </div>
      </div>

      {error && <div style={s.alertError}>{error}</div>}
      {successMsg && <div style={s.alertSuccess}>{successMsg}</div>}

      <div style={s.lowStockNote}>
        <span><span style={s.legendDot('#fef3c7')} />Low Stock (at or below reorder level)</span>
        <span><span style={s.legendDot('#fecaca')} />Out of Stock</span>
      </div>

      {loading ? (
        <p style={{ color: '#6b7280' }}>Loading...</p>
      ) : filtered.length === 0 ? (
        <p style={{ color: '#6b7280' }}>No spare parts found.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Part Code</th>
                <th style={s.th}>Part Name</th>
                <th style={s.th}>Category</th>
                <th style={s.th}>UoM</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Current Stock</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Reorder Level</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Unit Cost</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(row => (
                <tr key={row.id} style={stockRowStyle(row)}>
                  <td style={s.td}><strong>{row.part_code}</strong></td>
                  <td style={s.td}>{row.part_name}</td>
                  <td style={s.td}>{row.category}</td>
                  <td style={s.td}>{row.uom}</td>
                  <td style={{ ...s.td, textAlign: 'right', fontWeight: 600 }}>{Number(row.current_stock) || 0}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}>{Number(row.reorder_level) || 0}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}>{formatCurrency(row.unit_cost)}</td>
                  <td style={s.td}>
                    <span style={s.badge(row.is_active !== false)}>
                      {row.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
                    <button
                      style={{ ...s.btn, ...s.btnSecondary, ...s.btnSmall, marginRight: 4 }}
                      onClick={() => openEdit(row)}
                    >Edit</button>
                    <button
                      style={{ ...s.btn, ...s.btnSuccess, ...s.btnSmall }}
                      onClick={() => openStockAdjust(row)}
                    >Stock Adjust</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ---- Create / Edit Modal ---- */}
      {showModal && (
        <div style={s.overlay} onClick={() => setShowModal(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitle}>{editingId ? 'Edit Spare Part' : 'Add Spare Part'}</h3>

            <div style={s.formRow}>
              <div style={{ ...s.formGroup, ...s.formCol }}>
                <label style={s.label}>Part Code *</label>
                <input style={s.input} value={form.part_code}
                  onChange={e => setForm(f => ({ ...f, part_code: e.target.value }))} />
              </div>
              <div style={{ ...s.formGroup, ...s.formCol }}>
                <label style={s.label}>Part Name *</label>
                <input style={s.input} value={form.part_name}
                  onChange={e => setForm(f => ({ ...f, part_name: e.target.value }))} />
              </div>
            </div>

            <div style={s.formRow}>
              <div style={{ ...s.formGroup, ...s.formCol }}>
                <label style={s.label}>Category</label>
                <select style={s.select} value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ ...s.formGroup, ...s.formCol }}>
                <label style={s.label}>Unit of Measure</label>
                <select style={s.select} value={form.uom}
                  onChange={e => setForm(f => ({ ...f, uom: e.target.value }))}>
                  {UOM_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>

            <div style={s.formRow}>
              <div style={{ ...s.formGroup, ...s.formCol }}>
                <label style={s.label}>Current Stock</label>
                <input style={s.input} type="number" min="0" value={form.current_stock}
                  onChange={e => setForm(f => ({ ...f, current_stock: e.target.value }))} />
              </div>
              <div style={{ ...s.formGroup, ...s.formCol }}>
                <label style={s.label}>Reorder Level</label>
                <input style={s.input} type="number" min="0" value={form.reorder_level}
                  onChange={e => setForm(f => ({ ...f, reorder_level: e.target.value }))} />
              </div>
              <div style={{ ...s.formGroup, ...s.formCol }}>
                <label style={s.label}>Unit Cost</label>
                <input style={s.input} type="number" min="0" step="0.01" value={form.unit_cost}
                  onChange={e => setForm(f => ({ ...f, unit_cost: e.target.value }))} />
              </div>
            </div>

            <div style={s.formGroup}>
              <label style={s.label}>Storage Location</label>
              <input style={s.input} value={form.storage_location}
                placeholder="e.g. Storeroom B, Shelf 3"
                onChange={e => setForm(f => ({ ...f, storage_location: e.target.value }))} />
            </div>

            <div style={{ ...s.formGroup, display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={form.is_active}
                onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} id="chkActive" />
              <label htmlFor="chkActive" style={{ fontSize: 13, cursor: 'pointer' }}>Is Active</label>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button style={{ ...s.btn, ...s.btnSecondary }} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={{ ...s.btn, ...s.btnPrimary }} onClick={handleSave}>
                {editingId ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Stock Adjust Modal ---- */}
      {showAdjustModal && adjustTarget && (
        <div style={s.overlay} onClick={() => setShowAdjustModal(false)}>
          <div style={s.modalSmall} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitle}>Stock Adjustment</h3>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>
              <strong>{adjustTarget.part_code}</strong> — {adjustTarget.part_name}<br />
              Current Stock: <strong>{Number(adjustTarget.current_stock) || 0}</strong>
            </p>

            <div style={s.formGroup}>
              <label style={s.label}>Adjustment (+/-)</label>
              <input style={s.input} type="number" value={adjustForm.adjustment}
                placeholder="e.g. 10 or -5"
                onChange={e => setAdjustForm(f => ({ ...f, adjustment: e.target.value }))} />
              {adjustForm.adjustment !== 0 && adjustForm.adjustment !== '' && (
                <span style={{ fontSize: 11, color: '#6b7280', marginTop: 2, display: 'block' }}>
                  New stock will be: {(Number(adjustTarget.current_stock) || 0) + (Number(adjustForm.adjustment) || 0)}
                </span>
              )}
            </div>

            <div style={s.formGroup}>
              <label style={s.label}>Reason *</label>
              <textarea
                style={{ ...s.input, minHeight: 60, resize: 'vertical' }}
                value={adjustForm.reason}
                placeholder="Reason for adjustment..."
                onChange={e => setAdjustForm(f => ({ ...f, reason: e.target.value }))}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button style={{ ...s.btn, ...s.btnSecondary }} onClick={() => setShowAdjustModal(false)}>Cancel</button>
              <button style={{ ...s.btn, ...s.btnPrimary }} onClick={handleStockAdjust}>Apply Adjustment</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MaintSpareParts;
